import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import {
  acknowledgeGovernancePpm,
  acknowledgeGovernanceValidation,
  ensureGovernancePackage,
  fetchGovernanceWorkbench,
} from './CAGovernanceApi';
import { GovernanceWorkbench, SourceCode } from './CAGovernanceTypes';

const money = (v: unknown) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v) +
      ' XOF'
    : '—';
const date = (v?: string | null) => (v ? new Date(v).toLocaleString() : '—');
const badgeColor = (
  value?: string
): 'success' | 'warning' | 'info' | 'default' =>
  value === 'ACKNOWLEDGED' || value === 'ADOPTED' || value === 'VALIDATED'
    ? 'success'
    : value === 'FROZEN' || value === 'SUBMITTED'
      ? 'info'
      : value === 'PENDING' || value === 'DRAFT'
        ? 'warning'
        : 'default';
const Status = ({ value }: { value?: string | null }) => (
  <Chip
    size="small"
    color={badgeColor(value || '')}
    variant="outlined"
    label={value || '—'}
  />
);
const Metric = ({ label, value }: { label: string; value: string }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

type State = {
  model: GovernanceWorkbench | null;
  loading: boolean;
  busy: boolean;
  error: string;
  success: string;
  tab: number;
  confirm: null | 'VALIDATION' | 'PPM' | 'CREATE';
};

class CAGovernanceWorkbench extends React.Component<any, State> {
  state: State = {
    model: null,
    loading: false,
    busy: false,
    error: '',
    success: '',
    tab: 0,
    confirm: null,
  };

  private loadSequence = 0;

  private resolvePlanningCycleId = (props: any): number => {
    return Number(
      props.plancontext?.pci ??
      props.planningContext?.pci ??
      props.params?.pci ??
      props.planningcycleid ??
      props.pci ??
      0
    );
  };

  get pci(): number {
    return this.resolvePlanningCycleId(this.props);
  }

  componentDidMount() {
    void this.load();
  }

  componentDidUpdate(prevProps: any) {
    const previousPci = this.resolvePlanningCycleId(prevProps);
    const currentPci = this.resolvePlanningCycleId(this.props);

    if (previousPci !== currentPci) {
      void this.load();
    }
  }

  componentWillUnmount() {
    ++this.loadSequence;
  }

  load = async () => {
    const pci = this.pci;
    const sequence = ++this.loadSequence;

    if (!Number.isInteger(pci) || pci <= 0) {
      this.setState({
        model: null,
        loading: false,
        error: 'Planning Cycle context (pci) is required.',
      });
      return;
    }

    this.setState({
      loading: true,
      error: '',
    });

    try {
      const model = await fetchGovernanceWorkbench(pci);

      if (sequence !== this.loadSequence) {
        return;
      }

      this.setState({
        model,
        error: '',
      });
    } catch (e: any) {
      if (sequence !== this.loadSequence) {
        return;
      }

      this.setState({
        model: null,
        error: e?.message || String(e),
      });
    } finally {
      if (sequence === this.loadSequence) {
        this.setState({
          loading: false,
        });
      }
    }
  };

  act = async () => {
    const action = this.state.confirm;
    const m = this.state.model;
    if (!action || !m || this.state.busy) return;
    const allowed =
      action === 'CREATE'
        ? m.actions.canCreate
        : action === 'VALIDATION'
          ? m.actions.canAcknowledgeValidation
          : m.actions.canAcknowledgePpm;
    if (!allowed) {
      this.setState({
        confirm: null,
        error: 'Action is no longer eligible. Refresh the workbench.',
      });
      return;
    }
    this.setState({ busy: true, confirm: null, error: '', success: '' });
    try {
      if (action === 'CREATE') await ensureGovernancePackage(this.pci);
      else if (action === 'VALIDATION')
        await acknowledgeGovernanceValidation(this.pci);
      else await acknowledgeGovernancePpm(this.pci);
      await this.load();
      this.setState({
        success:
          action === 'CREATE'
            ? 'Governance package initialized.'
            : `${action} acknowledged successfully.`,
      });
    } catch (e: any) {
      this.setState({ error: e?.message || String(e) });
    } finally {
      this.setState({ busy: false });
    }
  };

  sourceCard = (code: SourceCode) => {
    const m = this.state.model!;
    const src = m.sources?.[code];
    const upstream = code === 'VALIDATION' ? m.validation : m.ppm;
    const eligible =
      code === 'VALIDATION'
        ? m.actions.canAcknowledgeValidation
        : m.actions.canAcknowledgePpm;
    const lifecycle =
      code === 'VALIDATION'
        ? m.validation?.sessionstatus
        : `${m.ppm?.ppmstage || '—'} / ${m.ppm?.ppmstatus || '—'}`;
    const amount =
      code === 'VALIDATION'
        ? m.validation?.budgetconsumption
        : m.ppm?.totalamount;
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {code === 'VALIDATION'
                ? 'DG Budget Validation'
                : 'Procurement Plan (PPM)'}
            </Typography>
            <Status value={src?.sourcestatus || 'PENDING'} />
          </Stack>
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
            {code === 'VALIDATION' ? m.validation?.recname : m.ppm?.recname}
          </Typography>
          <Typography sx={{ mt: 2 }} variant="body2">
            Upstream state: <strong>{lifecycle}</strong>
          </Typography>
          <Typography variant="body2">
            Source record: {src?.sourcerecid ?? 'Not acknowledged'}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {money(amount)}
          </Typography>
          {code === 'PPM' && (
            <Typography variant="caption" color="text.secondary">
              {m.ppm?.linecount ?? 0} procurement lines
            </Typography>
          )}
          {src?.acknowledgedat && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Acknowledged: {date(src.acknowledgedat)}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon />}
              disabled={!eligible || this.state.busy || this.state.loading}
              onClick={() => this.setState({ confirm: code })}
            >
              Acknowledge
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled
              title="Governance-controlled return is pending lifecycle and authorization qualification"
            >
              Return / Reopen
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  renderOverview = (m: GovernanceWorkbench) => (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Metric
            label="DG-validated budget"
            value={money(
              m.budgetversion?.apb ?? m.validation?.budgetconsumption
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Metric
            label="Economic capacity"
            value={money(
              m.budgetversion?.ecp ?? m.validation?.economiccapacity
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Metric
            label="Remaining capacity"
            value={money(
              m.budgetversion?.rcp ?? m.validation?.remainingcapacity
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>{this.sourceCard('VALIDATION')}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{this.sourceCard('PPM')}</Grid>
      </Grid>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography fontWeight={700}>Budget Version</Typography>
            <Typography variant="body2">
              {m.budgetversion
                ? `Version ${m.budgetversion.vno} · Record #${m.budgetversion.rid} · ${m.budgetversion.shc}`
                : 'No version generated'}
            </Typography>
          </Box>
          <Status value={m.budgetversion?.aps} />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="contained"
            disabled
            title="ADOPT endpoint and authorization not yet qualified"
          >
            Adopt Budget
          </Button>
          <Typography variant="caption" color="text.secondary">
            {m.actions.bothAcknowledged
              ? 'Both sources acknowledged; adoption remains pending backend qualification.'
              : 'Both sources must be acknowledged before adoption.'}
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );

  renderVersion = (m: GovernanceWorkbench) => {
    const v = m.budgetversion;
    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6">Frozen Budget Baseline</Typography>
          {!v ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No Budget Version available.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {(
                [
                  ['Version', String(v.vno)],
                  ['Record ID', String(v.rid)],
                  ['Approval stage', v.aps],
                  ['Snapshot code', v.shc],
                  ['Version type', v.vty],
                  ['Approved budget', money(v.apb)],
                  ['Requested budget', money(v.rqb)],
                  ['Created', date(v.stp)],
                ] as [string, string][]
              ).map(([k, value]) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={k}>
                  <Typography variant="caption" color="text.secondary">
                    {k}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
        <Alert severity="info">
          Detailed financial groups and lines require a separate read-only
          Budget Version detail endpoint. This screen does not substitute PPM
          lines for the full frozen budget.
        </Alert>
      </Stack>
    );
  };

  renderDecisions = (m: GovernanceWorkbench) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              'Date',
              'Action',
              'Source',
              'Actor',
              'Reason',
              'Meeting reference',
            ].map((x) => (
              <TableCell key={x}>{x}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {m.decisions.map((x) => (
            <TableRow key={x.recid}>
              <TableCell>{date(x.actionat)}</TableCell>
              <TableCell>{x.actioncode}</TableCell>
              <TableCell>{x.sourcecode}</TableCell>
              <TableCell>{x.actorid}</TableCell>
              <TableCell>{x.reason || '—'}</TableCell>
              <TableCell>{x.meetingreference || '—'}</TableCell>
            </TableRow>
          ))}
          {!m.decisions.length && (
            <TableRow>
              <TableCell colSpan={6}>No decisions recorded.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  renderHistory = (m: GovernanceWorkbench) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              'Recorded',
              'Version ID',
              'Status',
              'Validation ID',
              'Actor',
              'Reason',
            ].map((x) => (
              <TableCell key={x}>{x}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {m.versionhistory.map((x) => (
            <TableRow key={x.recid}>
              <TableCell>{date(x.recordedat)}</TableCell>
              <TableCell>{x.budgetversionid}</TableCell>
              <TableCell>
                <Status value={x.versionstatus} />
              </TableCell>
              <TableCell>{x.validationid ?? '—'}</TableCell>
              <TableCell>{x.recordedby}</TableCell>
              <TableCell>{x.reason}</TableCell>
            </TableRow>
          ))}
          {!m.versionhistory.length && (
            <TableRow>
              <TableCell colSpan={6}>No version history recorded.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  render() {
    const {
      model: m,
      loading,
      busy,
      error,
      success,
      tab,
      confirm,
    } = this.state;
    return (
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalanceOutlinedIcon color="primary" />
                <Typography variant="h5" fontWeight={700}>
                  CA Governance
                </Typography>
              </Stack>
              <Typography color="text.secondary" variant="body2">
                {m?.context?.planningcyclename || 'Planning Cycle'} · FY{' '}
                {m?.context?.fiscalyear || '—'} · Active journey phase:{' '}
                {m?.context?.phasename || '—'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {m?.package && <Status value={m.package.packagestatus} />}
              <Button
                startIcon={<RefreshIcon />}
                onClick={this.load}
                disabled={loading || busy}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                disabled={!m?.actions?.canCreate || busy || loading}
                onClick={() => this.setState({ confirm: 'CREATE' })}
              >
                Create Package
              </Button>
            </Stack>
          </Stack>
          {error && (
            <Alert
              severity="error"
              onClose={() => this.setState({ error: '' })}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              onClose={() => this.setState({ success: '' })}
            >
              {success}
            </Alert>
          )}
          {loading && <CircularProgress size={24} />}
          {m && (
            <>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Governance package
                    </Typography>
                    <Typography fontWeight={700}>
                      {m.package?.shortcode || 'Not initialized'} · Revision{' '}
                      {m.package?.revisionno ?? '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Submission readiness
                    </Typography>
                    <Typography variant="body2">
                      Validation:{' '}
                      {m.sources?.VALIDATION?.sourcestatus || 'PENDING'} · PPM:{' '}
                      {m.sources?.PPM?.sourcestatus || 'PENDING'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Budget version
                    </Typography>
                    <Typography fontWeight={600}>
                      {m.budgetversion
                        ? `#${m.budgetversion.rid} / ${m.budgetversion.aps}`
                        : 'Not available'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined">
                <Tabs
                  value={tab}
                  onChange={(_, value: number) => this.setState({ tab: value })}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Overview" />
                  <Tab label="Budget Version" />
                  <Tab label={`Decisions (${m.decisions.length})`} />
                  <Tab label={`History (${m.versionhistory.length})`} />
                </Tabs>
              </Paper>
              {tab === 0 && this.renderOverview(m)}
              {tab === 1 && this.renderVersion(m)}
              {tab === 2 && this.renderDecisions(m)}
              {tab === 3 && this.renderHistory(m)}
            </>
          )}
        </Stack>
        <Dialog
          open={!!confirm}
          onClose={() => !busy && this.setState({ confirm: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {confirm === 'CREATE'
              ? 'Initialize Governance Package'
              : `Acknowledge ${confirm === 'PPM' ? 'Procurement Plan' : 'DG Budget Validation'}`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirm === 'CREATE'
                ? 'Create or ensure the Governance package for this Planning Cycle?'
                : 'This records a formal Governance acknowledgment. Validation acknowledgment can generate a frozen Budget Version. Continue only after confirming the submitted source.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ confirm: null })}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={this.act} disabled={busy}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default CAGovernanceWorkbench;
